"use client";

import { useState } from "react";
import { cn } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import type { AskUserQuestionToolInfo } from "./types";
import { InteractiveToolCardShell } from "./AskUserQuestionCard";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import type { RequestPortfolioCardLabels } from "./resolveRequestPortfolioLabels";
import {
  REQUEST_PORTFOLIO_ACTION,
  type RequestPortfolioCardAction,
} from "./requestPortfolioAction";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { IS_PLAID_OPEN } from "@components/PlaidImportModal/openPlaidImportModal";

export { type RequestPortfolioCardAction };

export interface RequestPortfolioCardProps {
  labels: RequestPortfolioCardLabels;
  toolInfo?: AskUserQuestionToolInfo;
  className?: string;
  /** 非最后一条消息且无 result 时禁用交互 */
  disabled?: boolean;
  onAction: (action: RequestPortfolioCardAction) => void;
  /** 自由输入 Send 后回传 tool message */
  onFeedback?: (feedback: string) => void;
  trackingScene?: string;
}

const REQUEST_PORTFOLIO_INPUT_PLACEHOLDER = "Or tell me in your words...";
const REQUEST_PORTFOLIO_SEND_LABEL = "Send";

const ROW_CLASS =
  "flex w-full items-center justify-between py-4 text-left body_m text-content_primary";

const RequestPortfolioOptionRow = ({
  label,
  disabled,
  onClick,
  orLabel,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  orLabel?: string;
}) => (
  <button
    type="button"
    className={cn(ROW_CLASS, "disabled:cursor-not-allowed disabled:opacity-50")}
    disabled={disabled}
    data-openreplay-label={orLabel}
    onClick={onClick}
  >
    <span>{label}</span>
    <OspIcons
      name="arrow-right"
      size={16}
      color={ospThemes.colors.content_tertiary}
    />
  </button>
);

export const RequestPortfolioCard = ({
  labels,
  toolInfo,
  className,
  disabled = false,
  onAction,
  onFeedback,
  trackingScene,
}: RequestPortfolioCardProps) => {
  const [isOtherWaysExpanded, setIsOtherWaysExpanded] =
    useState(!IS_PLAID_OPEN);
  const [freeInput, setFreeInput] = useState("");
  const trimmedInput = freeInput.trim();
  const canSend = !disabled && trimmedInput.length > 0;
  const shouldTrackOnboarding = trackingScene === "onboarding";

  const handleAction = (
    action: RequestPortfolioCardAction,
    trackingPayload?: { method?: string },
  ) => {
    if (shouldTrackOnboarding) {
      if (action === REQUEST_PORTFOLIO_ACTION.CONNECT_PLAID) {
        // 同上：GA 双写，OpenReplay 单发 PRD 名
        trackOnboardingAnalytics("ob_plaid_clicked", undefined, {
          event: "ob_funnel_plaid_started",
        });
      }

      if (
        action === REQUEST_PORTFOLIO_ACTION.PDF_RECOGNIZED ||
        action === REQUEST_PORTFOLIO_ACTION.SCREENSHOT_RECOGNIZED
      ) {
        trackOnboardingAnalytics("ob_upload_method", {
          method: trackingPayload?.method,
        });
      }
    }

    onAction(action);
  };

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    const feedback = JSON.stringify({
      action: REQUEST_PORTFOLIO_ACTION.FEEDBACK,
      content: trimmedInput,
    });

    onFeedback?.(feedback);
  };

  return (
    <InteractiveToolCardShell
      title={labels.title}
      toolInfo={toolInfo}
      className={cn("mb-3", className)}
    >
      <div className="flex flex-col divide-y divide-[#d8ddd9]">
        {IS_PLAID_OPEN && (
          <RequestPortfolioOptionRow
            label={labels.connectLabel}
            disabled={disabled}
            orLabel={buildOpenReplayLabel("Onboarding", "Portfolio", "Plaid")}
            onClick={() => handleAction(REQUEST_PORTFOLIO_ACTION.CONNECT_PLAID)}
          />
        )}
        {!isOtherWaysExpanded && (
          <button
            type="button"
            className={cn(
              ROW_CLASS,
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            aria-expanded={isOtherWaysExpanded}
            disabled={disabled}
            onClick={() => setIsOtherWaysExpanded((prev) => !prev)}
          >
            <span>{labels.otherWaysLabel}</span>
            <OspIcons
              name="arrow-right"
              size={16}
              color={ospThemes.colors.content_tertiary}
              className={cn(
                "transition-transform duration-300 ease-in-out",
                isOtherWaysExpanded ? "rotate-180" : "rotate-0",
              )}
            />
          </button>
        )}
        {isOtherWaysExpanded ? (
          <>
            <RequestPortfolioOptionRow
              label={labels.uploadStatementLabel}
              disabled={disabled}
              orLabel={buildOpenReplayLabel(
                "Onboarding",
                "Portfolio",
                "UploadPdf",
              )}
              onClick={() =>
                handleAction(REQUEST_PORTFOLIO_ACTION.PDF_RECOGNIZED, {
                  method: "pdf",
                })
              }
            />
            <RequestPortfolioOptionRow
              label={labels.snapPhotoLabel}
              disabled={disabled}
              orLabel={buildOpenReplayLabel(
                "Onboarding",
                "Portfolio",
                "UploadScreenshot",
              )}
              onClick={() =>
                handleAction(REQUEST_PORTFOLIO_ACTION.SCREENSHOT_RECOGNIZED, {
                  method: "screenshot",
                })
              }
            />
            <RequestPortfolioOptionRow
              label={labels.skipLabel}
              disabled={disabled}
              orLabel={buildOpenReplayLabel("Onboarding", "Portfolio", "Skip")}
              onClick={() => handleAction(REQUEST_PORTFOLIO_ACTION.SKIPPED)}
            />
          </>
        ) : null}
      </div>
      <div
        className="flex items-center gap-[10px] py-3"
        data-testid="request-portfolio-card-input-row"
      >
        <input
          value={freeInput}
          onChange={(event) => setFreeInput(event.target.value)}
          className="min-w-0 flex-1 bg-transparent body_m rounded-2 border border-border_inverse_transparent10 p-3 text-content_primary placeholder:text-content_quaternary outline-none"
          placeholder={REQUEST_PORTFOLIO_INPUT_PLACEHOLDER}
          disabled={disabled}
          data-testid="request-portfolio-card-input"
        />
        {canSend ? (
          <button
            type="button"
            className="h-[44px] min-w-[86px] rounded-2 bg-background_brand_overlay10 px-[14px] text-[16px]/[24px] font-medium text-content_brand_primary disabled:cursor-not-allowed disabled:text-content_brand_tertiary"
            disabled={!canSend}
            onClick={handleSend}
            data-testid="request-portfolio-card-send"
          >
            {REQUEST_PORTFOLIO_SEND_LABEL}
          </button>
        ) : null}
      </div>
    </InteractiveToolCardShell>
  );
};
